import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { Field } from 'models';

import { MultiValueFieldMenuComponent } from './multi-value-field-menu/multi-value-field-menu.component';

/** Base field component. */
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class FieldBaseComponent {
  /**
   * Should the component add the required validator?
   *
   * Defaults to true.
   */
  addRequiredValidator = input(true);
  /**
   * Should the component add the regex validator?
   *
   * Defaults to true.
   */
  addRegexValidator = input(true);
  /**
   * Form control for the field.
   */
  control = input.required<UntypedFormControl>({ alias: 'form-control' });
  /**
   * Field.
   */
  field = input.required<Field>();

  /**
   * Emits when this field is blurred.
   */
  fieldBlurred = output<Field>();

  /**
   * Emits when this field is focused.
   */
  fieldFocused = output<Field>();

  /**
   * Multi-Value field menu component.
   */
  mvFieldMenu = input<MultiValueFieldMenuComponent>(undefined, {
    alias: 'mv-field-menu',
  });

  constructor(protected translate: TranslocoService) {}

  /**
   * Get form control error message.
   *
   * @returns A string containing the error message.
   */
  get errorMessage(): string {
    if (this.control().hasError('required')) {
      return this.translate.translate('REQUIRED_FIELD');
    }
    if (this.control().hasError('pattern')) {
      return this.regExMessageFromField;
    }
    if (this.control().hasError('matDatepickerParse')) {
      return this.translate.translate('INVALID_FIELD_DATE');
    }
    if (this.control().hasError('valueNotInList')) {
      return this.translate.translate('NOT_IN_LIST');
    }
    if (this.control().hasError('maxlength')) {
      const maxLengthError = this.control().getError('maxlength');
      return this.translate.translate('MAX_FIELD_LENGTH_ERROR', {
        maxLength: maxLengthError.requiredLength,
      });
    }

    return '';
  }

  /**
   * Get the validation message from the `Field`'s validation regular expression.
   *
   * @returns String containing the validation message, or an empty string if not available.
   */
  get regExMessageFromField(): string {
    return /(?<=\(\?#)(.*?)(?= #\))/.exec(this.field().regex)?.[0] ?? '';
  }

  /**
   * Adds all form validators to the form control.
   */
  addValidators(): void {
    this.control().addValidators(Validators.maxLength(this.field().length));
    if (this.field().required && this.addRequiredValidator()) {
      this.control().addValidators(Validators.required);
    }
    if (this.field().useRegExValidation && this.addRegexValidator()) {
      this.control().addValidators(
        Validators.pattern(this.getRegExPatternFromField())
      );
    }
  }

  /**
   * Get Reg Ex pattern from the field object.
   *
   * @returns String containing the regular expression.
   */
  getRegExPatternFromField(): string {
    let pattern = this.field().regex;
    if (this.field().useRegExValidation) {
      pattern = pattern
        .replace(/(?<=\(\?#)(.*?)(?= #\))/, '')
        .replace('(?# #)', '');
    }
    return pattern;
  }

  /**
   * Checks if the field should be blurred based on the event.
   *
   * @param event Event.
   * @returns True if the field should blur.
   */
  shouldBlur(event: FocusEvent): boolean {
    if (!event.relatedTarget) {
      return true;
    }

    /*
    Checks if the related target is an element that has the 'matSuffix' attribute.

    We do not want to blur the field if the user is just interacting with part of the form field because
    even material itself views the control as still in focus.
    The only exception is if the target is a mat-menu-item, because that means the user is interacting with the mv field menu
    which needs to allow the blur in order to function correctly.
    */
    const targetElement = event.relatedTarget as Element;
    return (
      !targetElement?.closest('[matSuffix]') &&
      !targetElement?.hasAttribute('mat-menu-item')
    );
  }

  /** Focus the underlying control. */
  abstract focus(): void;

  /**
   * Handles the blur event for the field input.
   *
   * @param event Focus event.
   */
  abstract onBlur(event: FocusEvent): void;

  /** Handles the focus event for the field input. */
  abstract onFocus(): void;
}
